import { BoundInfraServiceSelectors } from 'infra/modules/infraServiceModule';
import $ from 'jquery';
import R from 'ramda';
import { asyncHelper } from 'utils/asyncHelper';
import { HttpStatusHelper } from 'utils/HttpStatusHelper/HttpStatusHelper';
import 'utils/jQueryBinaryDataAjaxTransport';
import { BackendTypes } from 'utils/SentryUtils';

import { Environment } from 'Environment';
import {
    BoundCurrentUserActions,
    BoundCurrentUserSelectors,
} from 'reactApp/modules/CurrentUserModule';

import { SDKLogger } from '../../system/SDKLogger';
import { OpenStackIdentity } from './OpenStackIdentity';

const OS_INTERFACE = 'public';
const OS_IDENTITY_API_VERSION = 3;
const OpenStackRPC = {
    get(path, data, options) {
        return OpenStackRPC.call(path, data, options, 'GET');
    },

    post(path, data, options) {
        return OpenStackRPC.call(path, data, options, 'POST');
    },

    put(path, data, options) {
        return OpenStackRPC.call(path, data, options, 'PUT');
    },

    delete(path, data, options) {
        return OpenStackRPC.call(path, data, options, 'DELETE');
    },

    patch(path, data, options) {
        return OpenStackRPC.call(path, data, options, 'PATCH');
    },

    async call(url, data, options, httpMethod) {
        if (data && data._put) {
            httpMethod = 'PUT';
        }
        const {
            justRefreshedAuthToken,
        } = await OpenStackRPC._ensureAuthTokenExists();

        const performTargetRequest = () =>
            OpenStackRPC._performSingleRequest(url, httpMethod, data, options);

        try {
            return await performTargetRequest();
        } catch (firstAttemptError) {
            // for safety, now we check both 401 and 403 as recommended by Infra backend devs
            // TODO: separate 401 and 403 after switching to regular IAM (on Infra's roadmap right now)
            const authCheckFailed =
                HttpStatusHelper.isUnauthorized(firstAttemptError) ||
                HttpStatusHelper.isForbidden(firstAttemptError);

            const canAuthenticateAndRetry =
                authCheckFailed && !justRefreshedAuthToken;

            if (canAuthenticateAndRetry) {
                await OpenStackRPC._auth();

                return performTargetRequest();
            }
            throw firstAttemptError;
        }
    },

    async getTokenInfo() {
        await OpenStackRPC._ensureAuthTokenExists();
        const tokenInfoResponse = await OpenStackRPC.get(
            OpenStackIdentity.tokensUrl,
            {},
            { headers: { 'X-Subject-Token': OpenStackRPC._getAuthToken() } },
        );

        const { project, catalog, user } = tokenInfoResponse.body.token;
        const {
            id: OS_PROJECT_ID,
            name: OS_PROJECT_NAME,
            domain: { id: OS_PROJECT_DOMAIN_ID },
        } = project;
        const {
            name: OS_USERNAME,
            domain: { id: OS_USER_DOMAIN_ID, name: OS_USER_DOMAIN_NAME },
        } = user;
        const { endpoints } = catalog.find((item) => item.type === 'identity');
        const publicEndpoint = endpoints.find(
            (endpoint) => endpoint.interface === OS_INTERFACE,
        );
        const { url: OS_AUTH_URL, region: OS_REGION_NAME } = publicEndpoint;

        return {
            OS_PROJECT_ID,
            OS_PROJECT_NAME,
            OS_PROJECT_DOMAIN_ID,
            OS_USERNAME,
            OS_USER_DOMAIN_NAME,
            OS_USER_DOMAIN_ID,
            OS_AUTH_URL,
            OS_REGION_NAME,
            OS_INTERFACE,
            OS_IDENTITY_API_VERSION,
        };
    },

    async _ensureAuthTokenExists() {
        const authTokenExists = !!OpenStackRPC._getAuthToken();

        if (authTokenExists) {
            return { justRefreshedAuthToken: false };
        }

        await OpenStackRPC._auth();

        return { justRefreshedAuthToken: true };
    },

    _performSingleRequest(method, httpMethod, originalData, originalOptions) {
        const defaultOptions = {
            timeout: 65000,
            contentType: 'application/json',
            dataType: 'json',
        };

        const options = {
            ...defaultOptions,
            type: httpMethod,
            ...originalOptions,
            url: OpenStackRPC._resolveCurrentProjectId(method),
        };

        options.data = OpenStackRPC._serialize(originalData, httpMethod);

        options.headers = {
            ...options.headers,
            ..._getAuthHeaders(),
            ..._getEnvironmentSpecificHeaders(),
        };

        return _performRequestWithLogging({ method, options });
    },
};

OpenStackRPC._getAuthToken = function() {
    try {
        // TODO: extract into a separate service to work with session storage that uses some fallback inside
        return BoundCurrentUserSelectors.getInfraToken() || null;
    } catch (error) {
        // TODO: add regular error logging
        console.error(
            '>>>> OpenStackRPC.js#_getAuthToken()\t - window.sessionStorage.getItem error: ',
            error,
        );
    }
};

OpenStackRPC._setAuthToken = function(token) {
    try {
        // TODO: extract into a separate service to work with session storage that uses some fallback inside
        BoundCurrentUserActions.setInfraToken(token);
    } catch (error) {
        // TODO: add regular error logging
        console.error(
            '>>>> OpenStackRPC.js#_getAuthToken()\t - window.sessionStorage.setItem error: ',
            error,
        );
    }
};

OpenStackRPC._resolveCurrentProjectId = function(path) {
    if (path.includes('%projectId%')) {
        path = path.replace(
            '%projectId%',
            BoundInfraServiceSelectors.getOsProjectId(),
        );
    }

    return path;
};

async function _performRequestWithLogging({ method, options }) {
    const requestLogger = SDKLogger.createSingleRequestLogger({
        group: 'openstack-api',
        method,
        type: options.type,
        logResponseTextOnError: true,
        backendType: BackendTypes.OpenStack,
    });

    options.headers = {
        ...options.headers,
        ...requestLogger.getLoggingHeaders(),
    };

    let result, finalError;

    // account for first attempt
    const totalAttemptLimit = 1 + RETRY_LIMIT;

    for (
        let attemptCount = 0;
        attemptCount < totalAttemptLimit;
        attemptCount++
    ) {
        try {
            const { body, status, req } = await _performJqueryAjaxRequest(
                options,
            );

            requestLogger.logXhrOnResponse({ xhr: req });

            result = { body, status, req };
            break;
        } catch (requestOrError) {
            if (requestOrError instanceof AbortedError) {
                throw requestOrError;
            }

            const request = requestOrError;
            requestLogger.logXhrOnResponse({ xhr: request });
            const hasAttempts = attemptCount + 1 < totalAttemptLimit;
            const canRecoverByRetry =
                hasAttempts &&
                RECOVERABLE_ERROR_STATUSES.includes(request.status);
            if (canRecoverByRetry) {
                await asyncHelper.delay(RETRY_DELAY);
            } else {
                // FIXME: throw some error with request inside instead of just request
                finalError = request;
                break;
            }
        }
    }

    if (finalError) {
        throw finalError;
    } else {
        return result;
    }
}

export class AbortedError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AbortedError';
    }
}

function _performJqueryAjaxRequest({
    onprogress,
    saveAbortFunction,
    ...options
}) {
    if (getIsDataEmpty(options.data)) {
        delete options.data;
    }
    let isAbortedByUser = false;

    return new Promise((resolve, reject) => {
        $.ajax({
            ...options,

            beforeSend(xhr) {
                xhr.setRequestHeader('Cache-Control', 'no-cache');
            },

            success(body, status, req) {
                resolve({ body, status, req });
            },

            error(req) {
                reject(
                    isAbortedByUser
                        ? new AbortedError(
                              `request aborted by user, url = ${options.url}`,
                          )
                        : req,
                );
            },

            xhr() {
                const xhr = $.ajaxSettings.xhr();

                if (onprogress) {
                    xhr.upload.onprogress = onprogress;
                }
                if (saveAbortFunction) {
                    saveAbortFunction(() => {
                        isAbortedByUser = true;
                        xhr.abort();
                    });
                }

                return xhr;
            },
        });
    });
}

function getIsDataEmpty(data) {
    try {
        data = JSON.parse(data);
    } catch (error) {
        // ok
    }
    return R.isEmpty(data);
}

const RECOVERABLE_ERROR_STATUSES = [
    // request can be uncompleted because of some network error,
    // like WiFi loss or laptop being closed
    0,
    503,
];

const RETRY_DELAY = 3000;
// we don't want to retry for more than a minute now,
// but can't just check retry start time, because in some cases like laptop closing
// any amount of time can pass without us being able to retry,
// so we track retry attempts count instead
const RETRY_LIMIT = 60000 / RETRY_DELAY;

OpenStackRPC._serialize = function(originalData, httpMethod) {
    if (originalData instanceof Blob) {
        return originalData;
    }
    const dataCopy = Array.isArray(originalData)
        ? [...originalData]
        : { ...originalData };

    const shouldUseJson = ['POST', 'PUT', 'PATCH'].includes(httpMethod);

    return shouldUseJson ? JSON.stringify(dataCopy) : dataCopy;
};

let authPendingRequest;

OpenStackRPC._auth = function() {
    if (!authPendingRequest) {
        authPendingRequest = OpenStackRPC._makeAuthRequest();

        authPendingRequest
            .then((result) => {
                authPendingRequest = undefined;
                return result;
            })
            .catch((error) => {
                authPendingRequest = undefined;

                throw error;
            });
    }

    return authPendingRequest;
};

const AUTH_RETRY_TIME_MAX_MS = 5 * 60 * 1000;
const AUTH_RETRY_DELAY_MS = 2000;
const AUTH_RETRY_MAX_COUNT = Math.round(
    AUTH_RETRY_TIME_MAX_MS / AUTH_RETRY_DELAY_MS,
);

OpenStackRPC._makeAuthRequest = async function() {
    let retryCount = 0;
    while (true) {
        retryCount++;

        try {
            await OpenStackRPC._callAuthMethods();
            break;
        } catch (error) {
            if (
                retryCount >= AUTH_RETRY_MAX_COUNT ||
                Number(error.status) !== 401
            ) {
                throw error;
            }
        }

        await asyncHelper.delay(AUTH_RETRY_DELAY_MS);
    }
};

OpenStackRPC._callAuthMethods = async function() {
    await OpenStackRPC._clearAuthCookie();

    const interimAuthToken = await OpenStackRPC._refreshAuthCookieAndRetrieveInterimAuthToken();

    await OpenStackRPC._refreshRegularAuthToken(interimAuthToken);
};

OpenStackRPC._clearAuthCookie = () => {
    try {
        OpenStackRPC._callCookieUpdateUrl(OpenStackIdentity.logoutUrl);
    } catch (reason) {
        console.error('OpenStackRPC._clearAuthCookie - logout error:', reason);
    }
};

OpenStackRPC._refreshAuthCookieAndRetrieveInterimAuthToken = async () => {
    const tokenResponse = await OpenStackRPC._callCookieUpdateUrl(
        OpenStackIdentity.authUrl,
    );
    return _extractXSubjectTokenFromResponse(tokenResponse);
};

OpenStackRPC._refreshRegularAuthToken = async (interimAuthToken) => {
    const requestData = {
        auth: {
            identity: {
                methods: ['token'],
                token: { id: interimAuthToken },
            },
            scope: {
                project: {
                    id: BoundInfraServiceSelectors.getOsProjectId(),
                },
            },
        },
    };

    const tokenResponse = await OpenStackRPC._performSingleRequest(
        OpenStackIdentity.tokensUrl,
        'POST',
        requestData,
        {},
    );
    const regularAuthToken = _extractXSubjectTokenFromResponse(tokenResponse);
    OpenStackRPC._setAuthToken(regularAuthToken);
};

const _extractXSubjectTokenFromResponse = (response) =>
    response.req.getResponseHeader('x-subject-token');

OpenStackRPC._callCookieUpdateUrl = function(url) {
    // call an url just to update cookies in some way
    // - no result is needed, so we don't process response in any way
    // these urls are expected to return redirect, as that's not an API call, but an actual page request
    return OpenStackRPC._performSingleRequest(
        url,
        'GET',
        {},
        {
            // TODO: hide implementation - pass higher-level option like 'parseToJson: false'
            // don't apply any processing to response data
            dataType: 'text',

            // WORKAROUND: now it can happen that the backend can return an HTML error page, but with 200 status - we have to check for that on our side
            // FIXME: make backend ensure 500 on its errors, and remove this special case
            checkForHtmlErrorPage: true,
        },
    );
};

function _getAuthHeaders() {
    const authToken = OpenStackRPC._getAuthToken();

    return authToken ? { 'X-Auth-Token': authToken } : {};
}

function _getEnvironmentSpecificHeaders() {
    const environmentSpecificHeaders = {};

    if (Environment.isDefaultDevelopmentHost) {
        // local dev uses custom proxy server, this result in need of some additional workaround to avoid CORS problems
        // so we set additional headers for that proxy
        Object.assign(environmentSpecificHeaders, {
            'X-Forwarded-Port': window.location.port,
            'X-Forwarded-Host': window.location.hostname,
        });
    }

    return environmentSpecificHeaders;
}

export { OpenStackRPC };
